/**
 * React Query hooks for the Model Lab admin pages.
 *
 * Reads go straight to Supabase under admin RLS. Starting and cancelling a
 * run go to the middleware with the admin's Supabase access token (the
 * middleware holds the provider keys and runs the replays).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Json } from '@limo/database'
import { supabase } from '@/lib/supabase'
import type {
  CaseFilter,
  Contestant,
  LabelValue,
  ModelLabCaseDetail,
  ModelLabCaseSummary,
  ModelLabLabel,
  ModelLabResultDetail,
  ModelLabResultSummary,
  ModelLabRun,
  ProductionModels,
  ProviderInfo,
  Task,
} from '@/lib/model-lab'

const MIDDLEWARE_URL = (import.meta.env.VITE_GHL_MIDDLEWARE_URL as string | undefined) ?? null
const ACTIVE_POLL_MS = 4000
const PAGE = 1000

async function middlewareFetch<T>(path: string, init?: RequestInit): Promise<T> {
  if (!MIDDLEWARE_URL) throw new Error('VITE_GHL_MIDDLEWARE_URL is not configured')
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  if (!token) throw new Error('Not signed in')

  const response = await fetch(`${MIDDLEWARE_URL.replace(/\/+$/, '')}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...init?.headers },
  })
  const body = (await response.json().catch(() => ({}))) as { success?: boolean; data?: T; error?: string }
  if (!response.ok || body.success === false) {
    throw new Error(body.error ?? `Middleware request failed (${response.status})`)
  }
  return body.data as T
}

/** PostgREST caps a response at 1,000 rows; page until a short page. */
async function fetchAllPages<T>(page: (from: number, to: number) => PromiseLike<{ data: unknown; error: { message: string } | null }>): Promise<T[]> {
  const rows: T[] = []
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await page(from, from + PAGE - 1)
    if (error) throw new Error(error.message)
    const batch = (data ?? []) as T[]
    rows.push(...batch)
    if (batch.length < PAGE) return rows
  }
}

const isActive = (run: ModelLabRun | null | undefined) => run?.status === 'queued' || run?.status === 'running'

export function useModelLabProviders() {
  return useQuery<{ providers: ProviderInfo[]; maxCallsPerRun: number; productionModels?: ProductionModels }>({
    queryKey: ['admin', 'model-lab', 'providers'],
    queryFn: () => middlewareFetch('/api/model-lab/providers'),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

export function useModelLabRuns() {
  return useQuery<ModelLabRun[]>({
    queryKey: ['admin', 'model-lab', 'runs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('model_lab_runs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100)
      if (error) throw new Error(error.message)
      return (data ?? []) as unknown as ModelLabRun[]
    },
    refetchInterval: (query) => ((query.state.data ?? []).some(isActive) ? ACTIVE_POLL_MS : false),
  })
}

export function useModelLabRun(runId: string) {
  return useQuery<ModelLabRun | null>({
    queryKey: ['admin', 'model-lab', 'run', runId],
    queryFn: async () => {
      const { data, error } = await supabase.from('model_lab_runs').select('*').eq('id', runId).maybeSingle()
      if (error) throw new Error(error.message)
      return data as unknown as ModelLabRun | null
    },
    refetchInterval: (query) => (isActive(query.state.data) ? ACTIVE_POLL_MS : false),
  })
}

export function useModelLabCases(runId: string, poll: boolean) {
  return useQuery<ModelLabCaseSummary[]>({
    queryKey: ['admin', 'model-lab', 'cases', runId],
    queryFn: () =>
      fetchAllPages<ModelLabCaseSummary>((from, to) =>
        supabase
          .from('model_lab_cases')
          .select('id, task, case_key, location_id, production_output, source_created_at')
          .eq('run_id', runId)
          .order('source_created_at', { ascending: false })
          .range(from, to)
      ),
    refetchInterval: poll ? ACTIVE_POLL_MS : false,
  })
}

export function useModelLabResults(runId: string, poll: boolean) {
  return useQuery<ModelLabResultSummary[]>({
    queryKey: ['admin', 'model-lab', 'results', runId],
    queryFn: () =>
      fetchAllPages<ModelLabResultSummary>((from, to) =>
        supabase
          .from('model_lab_results')
          .select('id, case_id, contestant_key, provider, model, status, decision, input_tokens, output_tokens, latency_ms, cost_usd, error')
          .eq('run_id', runId)
          .order('created_at', { ascending: true })
          .range(from, to)
      ),
    refetchInterval: poll ? ACTIVE_POLL_MS : false,
  })
}

export function useModelLabCaseDetail(caseId: string | null) {
  return useQuery<{ labCase: ModelLabCaseDetail; results: ModelLabResultDetail[] } | null>({
    queryKey: ['admin', 'model-lab', 'case', caseId],
    enabled: caseId !== null,
    queryFn: async () => {
      if (!caseId) return null
      const [caseRes, resultsRes] = await Promise.all([
        supabase.from('model_lab_cases').select('*').eq('id', caseId).maybeSingle(),
        supabase.from('model_lab_results').select('*').eq('case_id', caseId).order('contestant_key'),
      ])
      if (caseRes.error) throw new Error(caseRes.error.message)
      if (resultsRes.error) throw new Error(resultsRes.error.message)
      if (!caseRes.data) return null
      return {
        labCase: caseRes.data as unknown as ModelLabCaseDetail,
        results: (resultsRes.data ?? []) as unknown as ModelLabResultDetail[],
      }
    },
  })
}

/** Labels for a location, keyed by case_key (labels outlive runs). */
export function useModelLabLabels(locationId: string | undefined) {
  return useQuery<Map<string, ModelLabLabel>>({
    queryKey: ['admin', 'model-lab', 'labels', locationId],
    enabled: Boolean(locationId),
    queryFn: async () => {
      const rows = await fetchAllPages<ModelLabLabel>((from, to) =>
        supabase.from('model_lab_labels').select('*').eq('location_id', locationId ?? '').range(from, to)
      )
      return new Map(rows.map((r) => [r.case_key, r]))
    },
  })
}

export interface StartRunInput {
  readonly locationId: string
  readonly tasks: Task[]
  readonly contestants: Contestant[]
  readonly caseFilter: CaseFilter
}

export function useStartModelLabRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: StartRunInput) =>
      middlewareFetch<{ runId: string }>('/api/model-lab/runs', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'model-lab', 'runs'] }),
  })
}

export function useCancelModelLabRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (runId: string) =>
      middlewareFetch<void>(`/api/model-lab/runs/${encodeURIComponent(runId)}/cancel`, { method: 'POST' }),
    onSuccess: (_data, runId) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'model-lab', 'run', runId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'model-lab', 'runs'] })
    },
  })
}

export interface SaveLabelInput {
  readonly task: Task
  readonly caseKey: string
  readonly locationId: string
  readonly label: LabelValue
  readonly notes?: string | null
}

export function useSaveModelLabLabel() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: SaveLabelInput) => {
      const { data: userData } = await supabase.auth.getUser()
      const { error } = await supabase.from('model_lab_labels').upsert(
        {
          task: input.task,
          case_key: input.caseKey,
          location_id: input.locationId,
          label: input.label as unknown as Json,
          notes: input.notes ?? null,
          labeled_by: userData.user?.id ?? null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'task,case_key' }
      )
      if (error) throw new Error(error.message)
    },
    onSuccess: (_data, input) =>
      queryClient.invalidateQueries({ queryKey: ['admin', 'model-lab', 'labels', input.locationId] }),
  })
}

export function useDeleteModelLabLabel() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { task: Task; caseKey: string; locationId: string }) => {
      const { error } = await supabase
        .from('model_lab_labels')
        .delete()
        .eq('task', input.task)
        .eq('case_key', input.caseKey)
      if (error) throw new Error(error.message)
    },
    onSuccess: (_data, input) =>
      queryClient.invalidateQueries({ queryKey: ['admin', 'model-lab', 'labels', input.locationId] }),
  })
}
